import type { Metric } from 'web-vitals';
import * as Sentry from '@sentry/react';
import logger from './loggerService';

/**
 * Enterprise-grade performance monitoring service.
 * Handles Core Web Vitals tracking and custom performance metrics.
 */
export class PerformanceService {
    private static isInitialized = false;

    /**
     * Initialize performance monitoring.
     * Should be called once at application startup.
     */
    public static init(): void {
        if (this.isInitialized) {
            return;
        }

        if (import.meta.env.PROD) {
            this.reportWebVitals();
        } else {
            console.log('📊 Performance monitoring initialized (Dev Mode: Logging to console)');
            this.reportWebVitals(true);
        }

        this.isInitialized = true;
    }

    /**
     * Report Core Web Vitals to Sentry or Console.
     * @param debug - If true, logs metrics to console instead of Sentry.
     */
    private static async reportWebVitals(debug = false): Promise<void> {
        try {
            const { onCLS, onINP, onLCP, onFCP, onTTFB } = await import('web-vitals');

            const sendToSentry = (metric: Metric) => {
                const { name, value, id, delta } = metric;

                // Send as a custom measurement to Sentry
                Sentry.setContext('performance', {
                    [name]: value,
                    [`${name}_id`]: id,
                });

                Sentry.addBreadcrumb({
                    category: 'performance',
                    message: `${name}: ${value} (delta: ${delta})`,
                    level: 'info',
                });

                // You can also use Sentry.metrics if enabled in your plan
                // Sentry.metrics.gauge(name, value, { tags: { id } });
            };

            const logToConsole = (metric: Metric) => {
                console.log(`⚡ ${metric.name}:`, metric.value, metric);
            };

            const handler = debug ? logToConsole : sendToSentry;

            onCLS(handler);
            onINP(handler); // Replaced FID with INP (web-vitals v4+)
            onLCP(handler);
            onFCP(handler);
            onTTFB(handler);
        } catch (error) {
            logger.error('Failed to load web-vitals', error, 'PerformanceService.reportWebVitals');
        }
    }

    /**
     * Mark a custom performance event.
     * @param name - Unique name for the mark.
     */
    public static mark(name: string): void {
        performance.mark(name);
    }

    /**
     * Measure the duration between two marks.
     * @param name - Name of the measure.
     * @param startMark - Name of the start mark.
     * @param endMark - Name of the end mark (defaults to now).
     */
    public static measure(name: string, startMark: string, endMark?: string): void {
        try {
            performance.measure(name, startMark, endMark);

            // Get the measure to log it
            const entries = performance.getEntriesByName(name);
            const latest = entries[entries.length - 1];

            if (latest) {
                if (import.meta.env.DEV) {
                    console.log(`⏱️ ${name}: ${latest.duration.toFixed(2)}ms`);
                }

                // Send to Sentry
                Sentry.addBreadcrumb({
                    category: 'performance.measure',
                    message: `${name}: ${latest.duration.toFixed(2)}ms`,
                    level: 'info',
                });
            }
        } catch (error) {
            logger.warn(`Failed to measure performance for ${name}`, { error, context: 'PerformanceService.measure' });
        }
    }
}

export default PerformanceService;
